/* =========================================================
   FlexiCompliance (BHT-samsvar) — TS models
   Shapes mirror the backend DTOs in
   Backend/Kuba2.Client.Entities/Flexi/Compliance/.
   ========================================================= */

export enum FlexiComplianceStatus {
  Draft = 0,
  InProgress = 1,
  Completed = 2,
  /**
   * Explicitly archived by an author. Archived rows are immutable snapshots
   * kept for the audit trail — they surface in the history grid's Archived tab,
   * open read-only, and are left off the Score-referanse chart. Nothing
   * archives a review automatically.
   */
  Archived = 3,
  /**
   * Replaced by a newer review. Read-only like Archived, but still part of the
   * business's history: it stays among the active rows and keeps its points on
   * the Score-referanse chart until an author archives it.
   */
  Superseded = 4,
}

/**
 * True for the statuses that are frozen history rather than the live review —
 * they open read-only, even for an author.
 */
export function isFlexiComplianceSnapshot(status: FlexiComplianceStatus): boolean {
  return status === FlexiComplianceStatus.Archived || status === FlexiComplianceStatus.Superseded;
}

/** 0 = Missing/red, 1 = NeedsImprovement/yellow, 2 = Good/green. */
export enum FlexiComplianceRating {
  Missing = 0,
  NeedsImprovement = 1,
  Good = 2,
}

/** 0 = Catalog snapshot, 1 = HSE-advisor-added custom item. */
export enum FlexiComplianceLineSource {
  Catalog = 0,
  Custom = 1,
}

/**
 * Lifecycle for an action point. Open → InProgress → Completed (reversible).
 *
 * `Rejected` is the terminal state for work that will never be done: starting
 * a new HSE review sweeps the still-outstanding action points into it, and a
 * CSM can also set it by hand. A live recurring series is spared — its open
 * instance was generated because the previous cycle was completed. Unlike
 * `Completed` it never promotes its checkpoint to green.
 */
export enum FlexiComplianceActionPointStatus {
  Open = 0,
  InProgress = 1,
  Completed = 2,
  Rejected = 3,
}

/** Who owns an action point. */
export enum FlexiComplianceResponsibleParty {
  Customer = 0,
  Advisor = 1,
}

/** Repeat interval of an action point. `None` is a one-off. */
export enum FlexiComplianceRecurrenceType {
  None = 0,
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Quarterly = 4,
  SemiAnnually = 5,
  Annually = 6,
  /** Multi-year statutory cycles, e.g. a five-yearly electrical inspection. */
  Every2Years = 7,
  Every3Years = 8,
  Every4Years = 9,
  Every5Years = 10,
}

/**
 * How the next deadline is computed when a recurring action point is completed
 * LATE. Only offered in that case — an on-time completion always keeps the
 * original schedule, and the server enforces that regardless of what is sent.
 */
export enum FlexiComplianceScheduleMode {
  /** Keep the series where it is; roll forward past any cycle already gone. */
  OriginalSchedule = 0,
  /** Re-anchor the series on today, permanently moving the cycle. */
  ShiftSchedule = 1,
}

/** Every interval a user can pick, in the order the dropdown shows them. */
export const FLEXI_RECURRENCE_TYPES: FlexiComplianceRecurrenceType[] = [
  FlexiComplianceRecurrenceType.None,
  FlexiComplianceRecurrenceType.Daily,
  FlexiComplianceRecurrenceType.Weekly,
  FlexiComplianceRecurrenceType.Monthly,
  FlexiComplianceRecurrenceType.Quarterly,
  FlexiComplianceRecurrenceType.SemiAnnually,
  FlexiComplianceRecurrenceType.Annually,
  FlexiComplianceRecurrenceType.Every2Years,
  FlexiComplianceRecurrenceType.Every3Years,
  FlexiComplianceRecurrenceType.Every4Years,
  FlexiComplianceRecurrenceType.Every5Years,
];

/** Translation key for an interval, for filter chips and dropdowns. */
export function recurrenceTypeKey(type: FlexiComplianceRecurrenceType): string {
  switch (type) {
    case FlexiComplianceRecurrenceType.Daily: return 'FLEXI_COMPLIANCE.RECURRENCE.DAILY';
    case FlexiComplianceRecurrenceType.Weekly: return 'FLEXI_COMPLIANCE.RECURRENCE.WEEKLY';
    case FlexiComplianceRecurrenceType.Monthly: return 'FLEXI_COMPLIANCE.RECURRENCE.MONTHLY';
    case FlexiComplianceRecurrenceType.Quarterly: return 'FLEXI_COMPLIANCE.RECURRENCE.QUARTERLY';
    case FlexiComplianceRecurrenceType.SemiAnnually: return 'FLEXI_COMPLIANCE.RECURRENCE.SEMI_ANNUALLY';
    case FlexiComplianceRecurrenceType.Annually: return 'FLEXI_COMPLIANCE.RECURRENCE.ANNUALLY';
    case FlexiComplianceRecurrenceType.Every2Years: return 'FLEXI_COMPLIANCE.RECURRENCE.EVERY_2_YEARS';
    case FlexiComplianceRecurrenceType.Every3Years: return 'FLEXI_COMPLIANCE.RECURRENCE.EVERY_3_YEARS';
    case FlexiComplianceRecurrenceType.Every4Years: return 'FLEXI_COMPLIANCE.RECURRENCE.EVERY_4_YEARS';
    case FlexiComplianceRecurrenceType.Every5Years: return 'FLEXI_COMPLIANCE.RECURRENCE.EVERY_5_YEARS';
    default: return 'FLEXI_COMPLIANCE.RECURRENCE.NONE';
  }
}

/** Matches FlexiComplianceActionPointResponse. */
export interface FlexiComplianceActionPoint {
  id: number;
  flexiComplianceLineItemId: number | null;
  title: string;
  description: string | null;
  responsibleParty: FlexiComplianceResponsibleParty | null;
  /** ISO date string yyyy-MM-dd. */
  dueDate: string | null;
  status: FlexiComplianceActionPointStatus;
  /** ISO datetime string. */
  completedOn: string | null;
  completedByUserId: number | null;
  completedByName: string | null;
  createdBy: number | null;
  createdByName: string | null;
  /** ISO datetime string. */
  createdOn: string;

  /** Named assignee. Null when only a responsible PARTY is set. */
  responsibleUserId: number | null;
  responsibleUserName: string | null;

  /** Follow-up person, snapshotted from the business's HSE advisor. */
  hseAdvisorUserId: number | null;
  hseAdvisorName: string | null;

  recurrenceType: FlexiComplianceRecurrenceType;
  /** Server-localized interval name — no client-side enum table needed. */
  recurrenceName: string | null;
  /** Stable id shared by every instance of the series. Null for one-offs. */
  seriesId: number | null;
  parentActionPointId: number | null;
  recurrenceCycleIndex: number;
  /**
   * Whole cycles that went unperformed before this instance existed. Non-zero
   * is a compliance-failure marker, surfaced in the grid.
   */
  skippedCycles: number;
  /** True when a user deliberately re-anchored the series on this deadline. */
  scheduleShifted: boolean;
}

/**
 * Both deadlines a late completion could produce, computed server-side.
 * The dialog shows real dates rather than describing the rules, so what the
 * user reads is by construction what gets written.
 */
export interface FlexiComplianceRecurrencePreview {
  actionPointId: number;
  recurrenceType: FlexiComplianceRecurrenceType;
  recurrenceName: string;
  currentDueDate: string | null;
  /** False → complete without prompting; the server keeps the original schedule. */
  requiresScheduleChoice: boolean;
  daysOverdue: number;
  /** Option A — keep the compliance cycle where it is. */
  originalScheduleNextDueDate: string;
  /** Cycles Option A jumps over. > 0 means whole cycles went unperformed. */
  originalScheduleSkippedCycles: number;
  /** Option B — re-anchor the series on today. */
  shiftScheduleNextDueDate: string;
}

/** One entry on an action point's audit timeline. */
export interface FlexiComplianceActionPointEvent {
  id: number;
  eventType: number;
  /** Already rendered in the caller's language by the server. */
  message: string;
  createdBy: number | null;
  createdByName: string | null;
  createdOn: string;
}

/**
 * One outstanding action point on the Handlingsplan dashboard card. Covers
 * recurring and one-off commitments alike, carrying the compliance markers so
 * the card can lead with what is wrong while still accounting for what is owed.
 */
export interface FlexiComplianceActionPointOverview {
  actionPointId: number;
  seriesId: number | null;
  title: string;
  /** 0 = Open, 1 = InProgress. Completed ones are not returned. */
  status: FlexiComplianceActionPointStatus;
  recurrenceType: FlexiComplianceRecurrenceType;
  /** Empty for a one-off commitment. */
  recurrenceName: string;
  dueDate: string | null;
  skippedCycles: number;
  scheduleShifted: boolean;
  daysOverdue: number;
  /** Server-computed: a missed cycle, a shifted schedule, or a passed deadline. */
  isException: boolean;
  /** 0 = Customer, 1 = Advisor. Null when the row never had a party set. */
  responsibleParty: FlexiComplianceResponsibleParty | null;
  /**
   * The responsible person. Falls back server-side to whoever currently holds
   * the relevant role, since most action points are assigned to a role rather
   * than to a named individual.
   */
  responsibleUserName: string | null;
  hseAdvisorName: string | null;
}

/** Request body for POST .../items/{id}/action-points. */
export interface AddActionPointRequest {
  title: string;
  description?: string | null;
  responsibleParty?: FlexiComplianceResponsibleParty | null;
  /** ISO date yyyy-MM-dd. */
  dueDate?: string | null;
  /** Named assignee. Optional — a party-only action point stays valid. */
  responsibleUserId?: number | null;
  /**
   * Non-zero makes this the first instance of a series, anchored on `dueDate`
   * — which the server therefore requires.
   */
  recurrenceType?: FlexiComplianceRecurrenceType;
}

/** Request body for PUT .../action-points/{id}. All fields optional. */
export interface UpdateActionPointRequest {
  title?: string | null;
  /** Empty string clears it; null leaves it unchanged. */
  description?: string | null;
  responsibleParty?: FlexiComplianceResponsibleParty | null;
  dueDate?: string | null;
  /**
   * Drops the deadline. A null `dueDate` means "leave unchanged", so removing
   * one needs this explicit flag. Ignored when `dueDate` is also set.
   */
  clearDueDate?: boolean;
  status?: FlexiComplianceActionPointStatus | null;
  /** Named assignee. Null leaves it unchanged; 0 clears it. */
  responsibleUserId?: number | null;
  /** Null leaves the recurrence unchanged. */
  recurrenceType?: FlexiComplianceRecurrenceType | null;
  /**
   * Which schedule to use when this request completes a recurring action point
   * LATE. Ignored for an on-time completion. Omitting it keeps the original
   * schedule, so a client can never accidentally shift a compliance cycle.
   */
  scheduleMode?: FlexiComplianceScheduleMode | null;
}

/**
 * Action point enriched with source context for the Flexi → Action Points
 * tab. `sectionId` / `sectionTitle` / `sourceQuestion` are all null for
 * loose action points; all set when the AP is attached to a line item.
 */
export interface FlexiComplianceActionPointListItem extends FlexiComplianceActionPoint {
  sectionId: number | null;
  sectionTitle: string | null;
  sourceQuestion: string | null;

  /**
   * Current rating of the originating checkpoint — which finding put this row
   * on the action plan. Null for a loose action point and for an unrated
   * checkpoint. Read live on the server, so it turns green the moment the
   * action closes.
   */
  sourceRating: FlexiComplianceRating | null;

  /** The compliance review this action point was captured under. */
  flexiComplianceId: number;
  /** Catalog version of that review, e.g. "BHT-2026-2029". */
  catalogVersionCode: string;
  /** ISO datetime. Distinguishes two runs of the same catalog version. */
  reviewCreatedOn: string;
  /** ISO datetime, or null while the review is still running. */
  reviewCompletedOn: string | null;
  /** True when the action point belongs to the business's live review. */
  isCurrentReview: boolean;
}

/**
 * One row in the "Previous reviews" strip. Persisted at completion time
 * (FinalSmiley/Percentage/etc. on FlexiCompliance) so the FE doesn't
 * recompute anything client-side.
 */
export interface FlexiComplianceHistoryItem {
  id: number;
  completedOn: string | null;
  completedByName: string | null;
  catalogVersionCode: string;
  templateId: number | null;
  templateName: string | null;
  isCurrent: boolean;
  percentage: number;
  smiley: 'red' | 'yellow' | 'green';
  greenCount: number;
  yellowCount: number;
  redCount: number;
  ratedCount: number;
  totalCount: number;
  generatedFeatureManualDocumentId: number | null;
}

/**
 * One row of the OHS Compliance Review history grid.
 *
 * Mirrors FlexiComplianceSummaryResponse. Unlike FlexiComplianceHistoryItem —
 * which backs the inline "previous reviews" strip and covers completed runs
 * only — this also carries the review currently in progress, so the score
 * fields are nullable.
 */
export interface FlexiComplianceSummary {
  id: number;
  businessId: number;
  status: FlexiComplianceStatus;
  /** True for the business's live (non-archived) review — the only editable one. */
  isCurrent: boolean;
  /** ISO datetime string. */
  createdOn: string;
  /** ISO date string (yyyy-MM-dd). End of the period the review covers. */
  periodTo: string | null;
  createdByName: string | null;
  modifiedByName: string | null;
  modifiedOn: string | null;
  completedOn: string | null;
  completedByName: string | null;
  catalogVersionCode: string;
  templateId: number | null;
  templateName: string | null;
  /** 0–100, 1 dp. Null until the review has been completed at least once. */
  percentage: number | null;
  smiley: 'red' | 'yellow' | 'green' | null;
  greenCount: number | null;
  yellowCount: number | null;
  redCount: number | null;
  ratedCount: number | null;
  totalCount: number | null;
  generatedFeatureManualDocumentId: number | null;
}

/**
 * One point on the Score-referanse timeline — the score frozen at a single
 * Complete of an assessment. Mirrors FlexiComplianceScorePointResponse.
 *
 * The important difference from FlexiComplianceSummary and
 * FlexiComplianceHistoryItem: both of those are one entry per REVIEW, carrying
 * only that review's latest score. Re-opening a completed review and
 * completing it again overwrites that score, which is why a chart built on
 * them replaced a repeated assessment's earlier data point instead of adding
 * one. This is one entry per COMPLETION, so the progression survives.
 */
export interface FlexiComplianceScorePoint {
  /** Snapshot id — unique per point, unlike `reviewId`. */
  id: number;
  /** The review this completion belongs to. Repeats across re-runs. */
  reviewId: number;
  /** 1-based completion number within the review: 1, then 2 after a re-run. */
  sequence: number;
  /** True when the review behind this point has been completed more than once. */
  isRepeat: boolean;
  /** ISO datetime string. */
  completedOn: string;
  completedByName: string | null;
  catalogVersionCode: string;
  templateId: number | null;
  templateName: string | null;
  /** True when the point belongs to the business's live (non-archived) review. */
  isCurrentReview: boolean;
  /** 0–100. */
  percentage: number;
  smiley: 'red' | 'yellow' | 'green';
  greenCount: number;
  yellowCount: number;
  redCount: number;
  ratedCount: number;
  totalCount: number;
  generatedFeatureManualDocumentId: number | null;
}

export interface FlexiComplianceLineItem {
  id: number;
  position: number;
  sourceType: FlexiComplianceLineSource;
  question: string;
  regulatoryReference: string | null;
  weight: number;
  rating: FlexiComplianceRating | null;
  comment: string | null;
  isRemoved: boolean;
  /** Action points attached to this question. Empty when none / all deleted. */
  actionPoints: FlexiComplianceActionPoint[];
}

export interface FlexiComplianceSection {
  id: number;
  position: number;
  title: string;
  translateKey: string | null;
  items: FlexiComplianceLineItem[];
}

export interface FlexiComplianceScore {
  percentage: number;
  /** "red" | "yellow" | "green" — drives the smiley + bar colour on the FE. */
  smiley: 'red' | 'yellow' | 'green';
  ratedCount: number;
  totalCount: number;
  greenCount: number;
  yellowCount: number;
  redCount: number;
  /** Checkpoints with no rating yet (`totalCount - ratedCount`). */
  unratedCount: number;

  /**
   * Weight-proportional segments of the dashboard ring, 0–100 with 1 dp,
   * always totalling exactly 100. Distinct from `percentage`, which gives
   * yellow half credit — these are the plain weight fractions per rating,
   * with `unratedShare` absorbing both the unassessed checkpoints and the
   * rounding remainder.
   */
  greenShare: number;
  yellowShare: number;
  redShare: number;
  unratedShare: number;
}

export interface FlexiCompliance {
  id: number;
  businessId: number;
  status: FlexiComplianceStatus;
  periodFrom: string | null;
  periodTo: string | null;
  assessedOn: string | null;
  completedOn: string | null;
  assessedByUserId: number | null;
  assessedByName: string | null;
  catalogVersionCode: string;
  catalogVersionName: string;
  /** Template the assessment was seeded from. Null for legacy pre-templates rows. */
  templateId: number | null;
  score: FlexiComplianceScore;
  sections: FlexiComplianceSection[];
}

/** Request body for PUT /items/{id}. */
export interface RateComplianceItemRequest {
  rating: FlexiComplianceRating | null;
  comment: string | null;
}

/** Request body for POST /sections/{id}/items. */
export interface NewComplianceCustomItemRequest {
  question: string;
  regulatoryReference: string | null;
}


// ---------------------------------------------------------------------------
// Responsible-party display
// ---------------------------------------------------------------------------

/**
 * Anything that carries a responsible party plus the resolved names — action
 * points from the grid, the compliance views, and the dashboard card all
 * satisfy this.
 */
export interface HasResponsibleParty {
  responsibleParty?: FlexiComplianceResponsibleParty | null;
  responsibleUserName?: string | null;
  hseAdvisorName?: string | null;
}

/** i18n key for the role itself, with no name attached. */
export function responsiblePartyLabelKey(
  party: FlexiComplianceResponsibleParty | null | undefined,
): string {
  return party === FlexiComplianceResponsibleParty.Advisor
    ? 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR'
    : 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER';
}

/**
 * The person behind the role: the named assignee when there is one, otherwise
 * the current holder of that role (the BE fills that in). Null when the
 * business has nobody in the role at all.
 */
export function responsiblePartyName(item: HasResponsibleParty): string | null {
  const name = item.responsibleParty === FlexiComplianceResponsibleParty.Advisor
    ? (item.responsibleUserName ?? item.hseAdvisorName)
    : item.responsibleUserName;
  return name && name.trim() ? name : null;
}

/**
 * What gets appended to a translated role label, e.g. " - Kari Nordmann".
 *
 * Returned as a suffix rather than a whole composed string so the role half
 * stays in the `translate` pipe: a component that builds the full label has to
 * inject TranslateService and re-render it on language change, which the pipe
 * already does. Empty when the role has no holder — a trailing dash with
 * nothing after it is worse than showing the role alone.
 */
export function responsiblePartyNameSuffix(item: HasResponsibleParty): string {
  const name = responsiblePartyName(item);
  return name ? ` - ${name}` : '';
}

// ---------------------------------------------------------------------------
// Paged action points
// ---------------------------------------------------------------------------

/**
 * Whole-list tallies that arrive alongside a page of action points.
 *
 * Counted over every action point on the business, ignoring both the page and
 * the active filters — a filter chip has to say what selecting it would show,
 * and a skipped cycle must not be hideable by a filter.
 */
export interface FlexiComplianceActionPointListMetadata {
  allCount: number;
  recurringCount: number;
  oneOffCount: number;
  openCount: number;
  inProgressCount: number;
  completedCount: number;
  rejectedCount: number;
  /** Rows whose checkpoint is currently rated Mangler (red). */
  missingCount: number;
  /** Rows whose checkpoint is currently rated Bør forbedres (yellow). */
  needsImprovementCount: number;
  skippedCycleCount: number;
}

/** One page of action points. Mirrors the BE's PagedResponse<T, TMetadata>. */
export interface FlexiComplianceActionPointPage {
  items: FlexiComplianceActionPointListItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  metadata: FlexiComplianceActionPointListMetadata;
}

/** Query for one page of action points. */
export interface FlexiComplianceActionPointPageQuery {
  pageNumber: number;
  pageSize: number;
  /** Null = every action point, true = recurring only, false = one-offs only. */
  isRecurring?: boolean | null;
  statuses?: FlexiComplianceActionPointStatus[];
  /**
   * Ratings of the originating checkpoint to keep — the Mangler / Bør
   * forbedres filter. Rows raised under no checkpoint drop out whenever this
   * is set.
   */
  sourceRatings?: FlexiComplianceRating[];
  recurrenceType?: FlexiComplianceRecurrenceType | null;
  /**
   * Ask for whichever page holds this action point, ignoring `pageNumber`.
   * Backs the dashboard card's ?highlight= deep link — only the server knows
   * where the row falls in the ordering.
   */
  focusActionPointId?: number | null;
}


// ---------------------------------------------------------------------------
// Score smiley
// ---------------------------------------------------------------------------

/** The three score buckets the BE assigns. */
export type FlexiComplianceSmiley = 'red' | 'yellow' | 'green';

/**
 * Asset path for a score bucket's face. Shipped under `src/assets/images/`:
 *   happy-smile.png   → green  (81-100%)
 *   neutral-smile.png → yellow (51-80%)
 *   sad-smile.png     → red    (0-50%)
 *
 * Returns null for an absent score so a caller can render nothing rather than a
 * broken image — an unscored review has no face, and defaulting to the sad one
 * would state a result that does not exist.
 */
export function smileyImage(smiley: FlexiComplianceSmiley | null | undefined): string | null {
  switch (smiley) {
    case 'green':  return 'assets/images/happy-smile.png';
    case 'yellow': return 'assets/images/neutral-smile.png';
    case 'red':    return 'assets/images/sad-smile.png';
    default:       return null;
  }
}

/** i18n key of the one-word summary that goes with a bucket. */
export function smileyLabelKey(smiley: FlexiComplianceSmiley | null | undefined): string {
  switch (smiley) {
    case 'green':  return 'FLEXI_COMPLIANCE.SCORE_CHIP.GREEN_LABEL';
    case 'yellow': return 'FLEXI_COMPLIANCE.SCORE_CHIP.YELLOW_LABEL';
    case 'red':    return 'FLEXI_COMPLIANCE.SCORE_CHIP.RED_LABEL';
    default:       return '';
  }
}
